import { Box, Typography, Button, TextField, MenuItem, Chip, Grid, Divider, Stack } from '@mui/material';
import { Activity, AlertTriangle, User, Phone } from 'lucide-react';
import { useTheme } from '@mui/material/styles';
import { AccordionSection } from '../common/AccordionSection';
import { Patient } from '../../types';
import { useTranslation } from 'react-i18next';

interface VitalFormData {
  bloodGroup: string;
  allergies: string[];
  chronicConditions: string[];
}

interface PatientMedicalInfoSectionProps {
  expanded: string | false;
  onExpandChange: (panel: string) => void;
  displayPatient: Patient;
  patient: Patient;
  editingVitals: boolean;
  setEditingVitals: (v: boolean) => void;
  vitalsSaving: boolean;
  vitalFormData: VitalFormData;
  setVitalFormData: React.Dispatch<React.SetStateAction<VitalFormData>>;
  allergyInput: string;
  setAllergyInput: (v: string) => void;
  conditionInput: string;
  setConditionInput: (v: string) => void;
  handleSaveVitals: () => void;
  handleAddAllergy: () => void;
  handleRemoveAllergy: (index: number) => void;
  handleAddCondition: () => void;
  handleRemoveCondition: (index: number) => void;
  canEditVitals: boolean | string | null | undefined;
  isDark: boolean;
  /** The clinic's common allergy/condition pick-lists — same source PatientDetailsTabbedView.tsx
   *  uses (GET /api/common-allergies, /api/common-chronic-conditions), rendered as quick-add chips
   *  above the free-text entry so a doctor/staff member isn't always retyping the same handful of
   *  names. Optional and defaulted to empty so this component still works if a caller doesn't load
   *  them (e.g. a future embed that doesn't need the pick-list). */
  commonAllergies?: Array<{ id: string; allergyName: string }>;
  commonConditions?: Array<{ id: string; conditionName: string }>;
  /** True when the corresponding list above failed to load (network/auth/server error) rather
   *  than genuinely being empty — surfaced so a doctor/staff member isn't left wondering why the
   *  quick-add chips are missing versus this clinic simply not having any configured yet. */
  commonAllergiesError?: boolean;
  commonConditionsError?: boolean;
}

function ChipList({
  items,
  color,
  onDelete,
}: {
  items: string[];
  color: 'error' | 'info';
  onDelete?: (index: number) => void;
}) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {items.map((item, index) => (
        <Chip
          key={index}
          label={item}
          size="small"
          color={color}
          variant={onDelete ? undefined : 'outlined'}
          onDelete={onDelete ? () => onDelete(index) : undefined}
        />
      ))}
    </Box>
  );
}

function AddChipRow({
  value,
  onChange,
  onAdd,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  onAdd: () => void;
  placeholder: string;
}) {
  const { t } = useTranslation('patients');
  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <TextField
        fullWidth
        size="small"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyPress={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); onAdd(); }
        }}
      />
      <Button variant="outlined" size="small" onClick={onAdd} sx={{ whiteSpace: 'nowrap' }}>
        {t('add')}
      </Button>
    </Box>
  );
}

export function PatientMedicalInfoSection({
  expanded,
  onExpandChange,
  displayPatient,
  patient,
  editingVitals,
  setEditingVitals,
  vitalsSaving,
  vitalFormData,
  setVitalFormData,
  allergyInput,
  setAllergyInput,
  conditionInput,
  setConditionInput,
  handleSaveVitals,
  handleAddAllergy,
  handleRemoveAllergy,
  handleAddCondition,
  handleRemoveCondition,
  canEditVitals,
  isDark,
  commonAllergies = [],
  commonConditions = [],
  commonAllergiesError = false,
  commonConditionsError = false,
}: PatientMedicalInfoSectionProps) {
  const { t } = useTranslation('patients');
  const theme = useTheme();

  return (
    <AccordionSection
      panelId="medical-info"
      expanded={expanded}
      onExpandChange={onExpandChange}
      icon={<Activity size={20} color={theme.palette.primary.main} />}
      title={t('medicalInformation')}
      headerActions={
        canEditVitals && !editingVitals ? (
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => { e.stopPropagation(); setEditingVitals(true); }}
          >
            {t('edit')}
          </Button>
        ) : undefined
      }
      detailsSx={{ bgcolor: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)' }}
    >
      {editingVitals ? (
        <Stack spacing={3}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>{t('bloodGroup')}</Typography>
            <TextField
              select fullWidth size="small"
              value={vitalFormData.bloodGroup}
              onChange={(e) => setVitalFormData(prev => ({ ...prev, bloodGroup: e.target.value }))}
            >
              <MenuItem value="">{t('selectBloodGroup')}</MenuItem>
              {['O+', 'O-', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-'].map(bg => (
                <MenuItem key={bg} value={bg}>{bg}</MenuItem>
              ))}
            </TextField>
          </Box>

          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>{t('allergies')}</Typography>
            <Stack spacing={1}>
              {commonAllergies.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    {t('commonAllergies')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {commonAllergies.map((allergy) => (
                      <Chip
                        key={allergy.id}
                        label={allergy.allergyName}
                        onClick={() => {
                          if (!vitalFormData.allergies.includes(allergy.allergyName)) {
                            setVitalFormData((prev) => ({ ...prev, allergies: [...prev.allergies, allergy.allergyName] }));
                          }
                        }}
                        variant={vitalFormData.allergies.includes(allergy.allergyName) ? 'filled' : 'outlined'}
                        color={vitalFormData.allergies.includes(allergy.allergyName) ? 'error' : 'default'}
                        size="small"
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
              {commonAllergiesError && (
                <Typography variant="caption" color="error">
                  {t('failedToLoadCommonAllergies')}
                </Typography>
              )}
              <AddChipRow value={allergyInput} onChange={setAllergyInput} onAdd={handleAddAllergy} placeholder={t('enterCustomAllergy')} />
              <ChipList items={vitalFormData.allergies} color="error" onDelete={handleRemoveAllergy} />
            </Stack>
          </Box>

          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>{t('chronicConditions')}</Typography>
            <Stack spacing={1}>
              {commonConditions.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    {t('commonConditions')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {commonConditions.map((condition) => (
                      <Chip
                        key={condition.id}
                        label={condition.conditionName}
                        onClick={() => {
                          if (!vitalFormData.chronicConditions.includes(condition.conditionName)) {
                            setVitalFormData((prev) => ({ ...prev, chronicConditions: [...prev.chronicConditions, condition.conditionName] }));
                          }
                        }}
                        variant={vitalFormData.chronicConditions.includes(condition.conditionName) ? 'filled' : 'outlined'}
                        color={vitalFormData.chronicConditions.includes(condition.conditionName) ? 'success' : 'default'}
                        size="small"
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
              {commonConditionsError && (
                <Typography variant="caption" color="error">
                  {t('failedToLoadCommonConditions')}
                </Typography>
              )}
              <AddChipRow value={conditionInput} onChange={setConditionInput} onAdd={handleAddCondition} placeholder={t('enterCustomChronicCondition')} />
              <ChipList items={vitalFormData.chronicConditions} color="info" onDelete={handleRemoveCondition} />
            </Stack>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 2 }}>
            <Button variant="outlined" onClick={() => setEditingVitals(false)} disabled={vitalsSaving}>{t('cancel')}</Button>
            <Button variant="contained" onClick={handleSaveVitals} disabled={vitalsSaving}>
              {vitalsSaving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Stack>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Activity size={18} color="#d32f2f" />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('bloodGroup2')}</Typography>
              {displayPatient.bloodGroup
                ? <Chip label={displayPatient.bloodGroup} size="small" color="error" variant="outlined" />
                : <Typography variant="body2" color="text.secondary">{t('notSpecified')}</Typography>}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <AlertTriangle size={18} color="#ed6c02" />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{t('allergies2')}</Typography>
                {displayPatient.allergies && displayPatient.allergies.length > 0
                  ? <ChipList items={displayPatient.allergies as string[]} color="error" />
                  : <Typography variant="body2" color="text.secondary">{t('noKnownAllergies')}</Typography>}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Activity size={18} color="#0288d1" />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{t('chronicConditions2')}</Typography>
                {displayPatient.chronicConditions && displayPatient.chronicConditions.length > 0
                  ? <ChipList items={displayPatient.chronicConditions as string[]} color="info" />
                  : <Typography variant="body2" color="text.secondary">{t('noChronicConditionsReported')}</Typography>}
              </Box>
            </Box>
          </Grid>

          {patient.emergencyContact && (patient.emergencyContact.name || patient.emergencyContact.phone) && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <User size={18} color="#2e7d32" />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{t('emergencyContact2')}</Typography>
                  {patient.emergencyContact.name && (
                    <Typography variant="body2"><strong>{t('name')}</strong> {patient.emergencyContact.name}</Typography>
                  )}
                  {patient.emergencyContact.phone && (
                    <Typography variant="body2">
                      <Phone size={14} style={{ display: 'inline', marginRight: 4 }} />
                      {patient.emergencyContact.phone}
                    </Typography>
                  )}
                  {patient.emergencyContact.relation && (
                    <Typography variant="body2" color="text.secondary">({patient.emergencyContact.relation})</Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      )}
    </AccordionSection>
  );
}
